import { useMutation } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { BrandPlanPreparation } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

interface Data {
  brand_plan_preparation: BrandPlanPreparation
}

export interface Variables {
  prompt?: string
  signal?: AbortSignal
}

export interface Props {
  brandPlanId: number
}

export const createBrandPlanPreparation = async ({
  brandPlanId,
  workspaceId,
  prompt,
  signal,
}: Variables & Props & { workspaceId: number }) => {
  const res = await sendToApi<Data>(`/w/${workspaceId}/brand_plans/${brandPlanId}/preparations`, {
    method: 'POST',
    body: {
      prompt,
    },
    signal,
  })

  if (res.type === 'exception' && res.data instanceof DOMException && res.data.name === 'AbortError') {
    throw res.data
  }

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    if (res.data === 'cancel') {
      throw new DOMException('The operation was aborted.', 'AbortError')
    }

    throw new Error(res.data)
  }

  return res.data
}

export interface Options extends Omit<UseMutationOptions<BrandPlanPreparation, Error, Variables>, 'mutationFn'> {}

export const useCreateBrandPlanPreparation = ({ brandPlanId }: Props, options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()

  return useMutation<BrandPlanPreparation, Error, Variables>(async (vars) => {
    const { prompt, signal } = vars

    const result = await createBrandPlanPreparation({
      workspaceId,
      prompt,
      signal,
      brandPlanId,
    })

    return result.brand_plan_preparation
  }, options)
}
